import React, { useEffect, useRef, useState } from "react";
import { MoreHorizontal, Pencil, MoveRight, Trash2, Bookmark } from "lucide-react";

/**
 * Compact 3-dots popover used on stories, booked trips, lounge posts, etc.
 * Pass any combination of onEdit / onMove / onDelete / onSave — buttons render
 * only for the handlers that exist, so the same component works for "edit +
 * move + delete" rows, folder rows (delete only), or lounge posts (save + edit + delete).
 *
 * The menu auto-closes on outside click or Escape.
 */
export default function RowMenu({
  onEdit,
  onMove,
  onDelete,
  onSave,
  testId = "row-menu",
  align = "right",
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const Item = ({ label, icon: Icon, onClick, danger, tid }) => (
    <button
      data-testid={tid}
      onClick={(e) => { e.stopPropagation(); setOpen(false); onClick(); }}
      className={`w-full flex items-center gap-2 px-3 py-2 text-sm font-hand text-left tb-tap ${
        danger
          ? "text-tb-coral hover:bg-tb-coral-soft"
          : "text-tb-navy hover:bg-tb-cream-2"
      }`}
    >
      <Icon className="w-4 h-4" />
      {label}
    </button>
  );

  return (
    <div ref={ref} className="relative shrink-0">
      <button
        data-testid={`${testId}-trigger`}
        aria-label="More actions"
        onClick={(e) => { e.stopPropagation(); setOpen((v) => !v); }}
        className="w-8 h-8 rounded-full flex items-center justify-center text-tb-navy/55 hover:text-tb-navy hover:bg-tb-cream-2 tb-tap"
      >
        <MoreHorizontal className="w-5 h-5" />
      </button>
      {open && (
        <div
          data-testid={`${testId}-menu`}
          className={`absolute z-30 top-9 ${align === "right" ? "right-0" : "left-0"} w-36 rounded-xl bg-tb-cream border-2 border-tb-cream-3 shadow-lg overflow-hidden`}
        >
          {onEdit && (
            <Item label="edit" icon={Pencil} onClick={onEdit} tid={`${testId}-edit`} />
          )}
          {onSave && (
            <Item label="save" icon={Bookmark} onClick={onSave} tid={`${testId}-save`} />
          )}
          {onMove && (
            <Item label="move" icon={MoveRight} onClick={onMove} tid={`${testId}-move`} />
          )}
          {onDelete && (
            <Item label="delete" icon={Trash2} onClick={onDelete} danger tid={`${testId}-delete`} />
          )}
        </div>
      )}
    </div>
  );
}
